import { useElementScrollPositions } from "@personeel/components/Werklijnen/hooks/useElementScrollPositions.ts";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore.ts";
import { DateTime, Interval } from "luxon";
import { Fragment, memo, useCallback, useEffect } from "react";
import { useDebounce } from "use-debounce";
import { calcCurrentTime, calcPosition } from "../../../utils/TijdlijnDateCalcUtils";
import { personeelsdienstLabelWidth } from "../Personeelsdiensten/PersoneelsdienstLabel/PersoneelsdientLabel";
import styles from "./Toolbar.module.css";

interface IUrenToolbar {
  readonly scale?: number;
  readonly werklijnenRef: React.RefObject<HTMLDivElement>;
}

export const UrenToolBar = memo(function UrenToolbar({ scale = 12, werklijnenRef }: IUrenToolbar) {
  const timeLineBegin = useSelectedTimeStore((state) => state.scenarioInterval?.start)!;
  const timeLineEnd = useSelectedTimeStore((state) => state.scenarioInterval?.end)!;
  const currentTime = useSelectedTimeStore((state) => state.selectedTime)!;
  const setCurrentTime = useSelectedTimeStore((state) => state.setDateTime);

  const hours = Interval.fromDateTimes(timeLineBegin, timeLineEnd)
    .splitBy({ hour: 1 })
    .map((d) => d.start!.hour);

  const hours24 = hours.slice(0, 24);

  const navigateToHour = useCallback(
    (date: DateTime, timeLineBegin: DateTime) => {
      const position = calcPosition(date, timeLineBegin, timeLineEnd, scale);
      if (werklijnenRef !== undefined && werklijnenRef.current !== null) {
        werklijnenRef.current.scrollTo({
          left: position - werklijnenRef.current?.clientWidth / 2 + personeelsdienstLabelWidth,
        });
      }
    },
    [scale, timeLineEnd, werklijnenRef],
  );

  useEffect(() => {
    navigateToHour(currentTime, timeLineBegin);
  }, [currentTime, navigateToHour, timeLineBegin]);

  const { posX } = useElementScrollPositions(werklijnenRef);
  const [debouncedPosX] = useDebounce(posX, 25);
  const debouncedCurrentTime = calcCurrentTime(
    debouncedPosX,
    werklijnenRef.current?.clientWidth ?? 0,
    timeLineBegin,
    scale,
  );

  useEffect(() => {
    setCurrentTime(
      calcCurrentTime(debouncedPosX, werklijnenRef.current?.clientWidth ?? 0, timeLineBegin, scale),
    );
  }, [debouncedPosX, scale, setCurrentTime, timeLineBegin, werklijnenRef]);

  return (
    <div className={styles["sub-toolbar"]}>
      <span className={styles["sub-toolbar-label"]}>Uur</span>
      <span className={styles["sub-toolbar-content"]}>
        {hours24.map((val, index) => {
          return (
            <Fragment key={val}>
              <button
                key={val}
                data-testid={`uren-toolbar-hour-${val}`}
                className={
                  styles["button"] +
                  " " +
                  (val === debouncedCurrentTime.hour % 24
                    ? styles["button-highlight"]
                    : styles["button-lowlight"])
                }
                onClick={() => {
                  navigateToHour(timeLineBegin.plus({ hour: index }), timeLineBegin);
                }}
              >
                {val}
              </button>
              {index + 1 < hours.length ? (
                <div key={"divider" + val} className={styles["button-group-divider"]} />
              ) : null}
            </Fragment>
          );
        })}
      </span>
    </div>
  );
});
